import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';

import { observer } from 'mobx-react';
import { Route, Router, Switch, useParams } from 'react-router-dom';

import { UnreachableFunction } from 'ui/base/types';
import { useStyles } from 'ui/base/theme';
import { HistoryModalStore, Modal } from 'ui/base/modal';
import { StandardPage } from 'ui/base/page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';
import { DefaultButton } from 'ui/public/button';

import { Optional } from 'services/base/types';
import { Pangolin } from 'services/accounts/types';
import { IPangolinDomain, UpdateHistoryRef, UpdateHistoryType, UpdateRelease } from 'services/pangolin/types';

import { themedStyles } from '../manage_updates';
import { UpdateUpgradeStepper } from '../ui/update_upgrade_stepper';
import { UpdateRestoreStepper } from '../ui/update_restore_stepper';

import Messages from './update_history_modal_messages';

export type UpdateHistoryInfoModalProps = UpdateHistoryModalProps;
export const UpdateHistoryInfoModal = observer(({ modal, domain }: UpdateHistoryInfoModalProps) => {
  const { id } = useParams<{ id: string }>();
  const styles = useStyles(themedStyles);

  const release = domain.updateReleases.get(id);

  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.versionInfoTitle()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldTextLeft} >
                {release?.tag}
                <div className={styles.fieldInfo}>
                  {Messages.versionInfoFieldVersion()}
                </div>
              </div>
              <img className={styles.fieldIcon}
                  src={styles.fieldIconImageVersion}
                  alt='Version Icon' />
            </div>
            {release?.notes && (
              <div className={styles.row}>
                <div className={classNames(styles.column, styles.corpus)}>
                  <div className={styles.label}>
                    {Messages.versionInfoFieldNotes()}
                  </div>
                  <div className={styles.value}>
                    <ReactMarkdown>{release.notes}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.section}>
            <DefaultButton type='button' onClick={modal.history.goBack}>
              {Messages.versionInfoButtonBack()}
            </DefaultButton>
          </div>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateHistoryStatusModalProps = UpdateHistoryModalProps;
export const UpdateHistoryStatusModal = observer(({
  pangolin,
  domain,
  item,
  modal,
  root,
}: UpdateHistoryStatusModalProps) => {
  const styles = useStyles(themedStyles);

  const source: Optional<UpdateRelease> = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: {
        const upgrade = domain.updateUpgrades.get(item.upgrade_id);
        return (
          !upgrade?.source_release_id ? undefined :
          domain.updateReleases.get(upgrade.source_release_id)
        );
      }
      case UpdateHistoryType.RESTORE: {
        const restore = domain.updateRestores.get(item.restore_id);
        return (
          !restore?.source_release_id ? undefined :
          domain.updateReleases.get(restore.source_release_id)
        );
      }
      default: {
        return UnreachableFunction(item);
      }
    }
  }, [item]);

  const target: Optional<UpdateRelease> = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: {
        const upgrade = domain.updateUpgrades.get(item.upgrade_id);
        return (
          !upgrade?.target_release_id ? undefined :
          domain.updateReleases.get(upgrade.target_release_id)
        );
      }
      case UpdateHistoryType.RESTORE: {
        const restore = domain.updateRestores.get(item.restore_id);
        return (
          !restore?.target_release_id ? undefined :
          domain.updateReleases.get(restore.target_release_id)
        );
      }
      default: {
        return UnreachableFunction(item);
      }
    }
  }, [item]);

  const onShowSourceModal = React.useCallback(() => {
    if (source) modal.history.push(`${root}/info/releases/${source.id}`);
  }, [source, modal, root]);

  const onShowTargetModal = React.useCallback(() => {
    if (target) modal.history.push(`${root}/info/releases/${target.id}`);
  }, [target, modal, root]);

  const itemTitle = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: return Messages.historyInfoTitleUpgrade();
      case UpdateHistoryType.RESTORE: return Messages.historyInfoTitleRestore();
      default: return UnreachableFunction(item);
    }
  }, [item]);

  const itemStepper = useMemo(() => {
    switch (item.type) {
      case UpdateHistoryType.UPGRADE: {
        const upgrade = domain.updateUpgrades.get(item.upgrade_id);
        return !upgrade ? undefined : <UpdateUpgradeStepper upgrade={upgrade} />;
      }
      case UpdateHistoryType.RESTORE: {
        const restore = domain.updateRestores.get(item.restore_id);
        return !restore ? undefined : <UpdateRestoreStepper restore={restore} />;
      }
      default: return UnreachableFunction(item);
    }
  }, [item]);

  return (
    <div className={classNames(styles.modalContainerBig, { [styles.modalContainerPopOut]: modal.visible })}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {itemTitle}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <div className={styles.modalContentRow}>
          <div className={styles.section}>
            <div className={classNames(styles.field, styles.item)}>
              <div className={styles.fieldTextLeft}>
                {pangolin.nickname}
                <div className={styles.fieldInfo}>
                  {Messages.historyInfoFieldPangolin()}
                </div>
              </div>
              <ShieldIcon
                className={styles.fieldIcon}
                colorCode={pangolin.colorCode}
                size={ShieldSize.SMALL}
              />
              <div className={classNames(styles.open, styles.disabled)} />
            </div>
            {source && (
              <div className={classNames(styles.field, styles.item, styles.clickable)} onClick={onShowSourceModal}>
                <div className={styles.fieldTextLeft}>
                  {source.tag}
                  <div className={styles.fieldInfo}>
                    {Messages.historyInfoFieldSource()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Source Icon'
                    src={styles.fieldIconImageSource} />
                <div className={styles.open} />
              </div>
            )}
            {target && (
              <div className={classNames(styles.field, styles.item, styles.clickable)} onClick={onShowTargetModal}>
                <div className={styles.fieldTextLeft}>
                  {target.tag}
                  <div className={styles.fieldInfo}>
                    {Messages.historyInfoFieldTarget()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Target Icon'
                    src={styles.fieldIconImageTarget} />
                <div className={styles.open} />
              </div>
            )}
          </div>
          <div className={styles.section}>
            {itemStepper}
          </div>
        </div>
      </StandardPage>
    </div>
  );
});

export type UpdateHistoryModalProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
  item: UpdateHistoryRef;
  locale: string;
  format: object;
  modal: HistoryModalStore;
  root: string;
};
export const UpdateHistoryModal = observer((props: UpdateHistoryModalProps) => {
  const { modal, root } = props;
  return (
    <Modal store={modal} onBackdropClick={modal.history.goBack}>
      <Router history={modal.history}>
        <Switch>
          <Route exact path={`${root}/status`}>
            <UpdateHistoryStatusModal {...props} />
          </Route>
          <Route exact path={`${root}/info/releases/:id`}>
            <UpdateHistoryInfoModal {...props} />
          </Route>
        </Switch>
      </Router>
    </Modal>
  );
});
